/**
 * Shared Alpine data for product cards:
 * variant switch, hover image preload, mobile image swipe.
 *
 * Usage: x-data="productCard({ isLoading, productFeaturedImageUrl, productFeaturedImageId, currentVariant, product, ...extras })"
 *
 * Nested Embla conflict: image swipe handlers stopPropagation when a variant image exists,
 * so parent Embla does not need watchDrag configuration.
 */
(function () {
  if (window.__productCardAlpineRegistered) return;
  window.__productCardAlpineRegistered = true;

  document.addEventListener("alpine:init", () => {
    Alpine.data("productCard", (config = {}) => {
      const {
        isLoading = true,
        productFeaturedImageUrl = "",
        productFeaturedImageId = "",
        currentVariant = null,
        product = { variants: [] },
        ...rest
      } = config;

      return {
        isLoading,
        productFeaturedImageUrl,
        productFeaturedImageId,
        currentVariant,
        product,
        hoverImageLoading: false,
        isHovering: false,
        _loadedHoverUrl: null,
        mobileSlideIndex: 0,
        touchStartX: 0,
        touchDeltaX: 0,
        isTouching: false,
        isSwiping: false,
        ...rest,
        get hasHoverImage() {
          const variant = this.currentVariant;
          if (!variant?.featured_image_url) return false;
          if (variant.featured_image_id && this.productFeaturedImageId) {
            return String(variant.featured_image_id) !== String(this.productFeaturedImageId);
          }
          return variant.featured_image_url !== this.productFeaturedImageUrl;
        },
        get isInStock() {
          const v = this.currentVariant;
          return !!(v && v.pack_available);
        },
        get mobileTrackStyle() {
          const offset = -this.mobileSlideIndex * 100;
          const drag = this.isTouching ? this.touchDeltaX : 0;
          return {
            transform: `translate3d(calc(${offset}% + ${drag}px), 0, 0)`,
            transition: this.isTouching ? "none" : "transform 0.3s ease-out",
          };
        },
        preloadHoverImage() {
          if (!this.hasHoverImage) {
            this.hoverImageLoading = false;
            return;
          }
          const url = this.currentVariant?.featured_image_url;
          if (!url) {
            this.hoverImageLoading = false;
            return;
          }
          if (this._loadedHoverUrl === url) {
            this.hoverImageLoading = false;
            return;
          }
          this.hoverImageLoading = true;
          const img = new Image();
          const done = () => {
            this._loadedHoverUrl = url;
            this.hoverImageLoading = false;
          };
          img.onload = done;
          img.onerror = done;
          img.src = url;
          if (img.complete) done();
        },
        setMobileSlide(index) {
          if (!this.hasHoverImage) {
            this.mobileSlideIndex = 0;
            return;
          }
          this.mobileSlideIndex = index === 1 ? 1 : 0;
          if (this.mobileSlideIndex === 1) this.preloadHoverImage();
        },
        /** Block parent Embla/pointer drag when image swipe is available. */
        onImagePointerDown(e) {
          if (!this.hasHoverImage) return;
          e.stopPropagation();
        },
        onTouchStart(e) {
          if (!this.hasHoverImage) return;
          e.stopPropagation();
          this.touchStartX = e.touches[0].clientX;
          this.touchDeltaX = 0;
          this.isTouching = true;
          this.isSwiping = false;
        },
        onTouchMove(e) {
          if (!this.isTouching || !this.hasHoverImage) return;
          e.stopPropagation();
          this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
          if (Math.abs(this.touchDeltaX) > 8) this.isSwiping = true;
        },
        onTouchEnd(e) {
          if (!this.isTouching) return;
          e?.stopPropagation?.();
          this.isTouching = false;
          if (!this.hasHoverImage) {
            this.touchDeltaX = 0;
            this.mobileSlideIndex = 0;
            return;
          }
          const threshold = 40;
          if (this.touchDeltaX < -threshold && this.mobileSlideIndex < 1) {
            this.setMobileSlide(1);
          } else if (this.touchDeltaX > threshold && this.mobileSlideIndex > 0) {
            this.setMobileSlide(0);
          }
          this.touchDeltaX = 0;
        },
        getCurrentVariant(id) {
          const variant = this.product.variants.find(i => i.id === id);
          if (!variant) return;
          this.currentVariant = variant;
          this._loadedHoverUrl = null;
          this.mobileSlideIndex = 0;
          this.touchDeltaX = 0;
          this.preloadHoverImage();
        },
      };
    });
  });
})();
